import * as Yup from 'yup';
import { createGroupAndUserAdmin, isNewGroup, updateAccessRequestStatus } from '@modules/api';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    TextField,
} from '@mui/material';
import { AccessRequest, getIdFromName, RequestState } from '@synergetx/puppet-interview-types';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { ChangeEvent, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '@modules/logging';
import { userMessage } from '@modules/api-error';
import { Grid } from '@mui/material';
import CopyToClipboardButton from './CopyToClipboardButton';
import { format } from 'date-fns/format';

interface CreateGroupWithUserForm {
    groupName: string;
}

const initialValues: CreateGroupWithUserForm = {
    groupName: '',
};

interface Props {
    onClose: () => void;
    requestToEdit: AccessRequest;
    setAllRequests: React.Dispatch<React.SetStateAction<AccessRequest[] | null>>;
}

const AccessReviewDialog = ({ requestToEdit, onClose, setAllRequests }: Props) => {
    const [denying, setDenying] = useState(false);

    const onSubmit = async (
        groupInfo: CreateGroupWithUserForm,
        { setSubmitting }: FormikHelpers<CreateGroupWithUserForm>,
    ) => {
        try {
            // Await the create so a failure leaves the dialog open rather than closing on a maybe.
            const res = await toast.promise(
                createGroupAndUserAdmin({
                    accessRequest: {
                        ...requestToEdit,
                        assignedGroupId: getIdFromName(groupInfo.groupName),
                        assignedGroupName: groupInfo.groupName,
                    },
                }),
                {
                    error: {
                        render: ({ data }) => userMessage(data, 'Error granting user access'),
                    },
                    pending: `Granting user access...`,
                    success: 'Success!',
                },
            );

            if (res.accessRequest) {
                setAllRequests(
                    // replace the access request with the new one
                    (prev) =>
                        (prev || [])?.map((r) =>
                            r.id === res.accessRequest?.id ? res.accessRequest : r,
                        ),
                );
            }

            onClose();
        } catch (error) {
            logError(error as Error);
        } finally {
            setSubmitting(false);
        }
    };

    const onDenyAccess = useCallback(async () => {
        setDenying(true);
        try {
            const response = await toast.promise(
                updateAccessRequestStatus({ id: requestToEdit.id, status: RequestState.DENIED }),
                {
                    error: { render: ({ data }) => userMessage(data, 'Error denying access') },
                    pending: `Denying access...`,
                    success: 'Request updated!',
                },
            );
            if (response) {
                setAllRequests(
                    // replace the access request with the new one
                    (prev) => (prev || [])?.map((r) => (r.id === response.id ? response : r)),
                );
            }
            onClose();
        } catch (error) {
            logError(error as Error);
        } finally {
            setDenying(false);
        }
    }, [requestToEdit.id, setAllRequests, onClose]);

    const requestDetails = (
        <>
            <Grid
                size={12}
                sx={{
                    paddingTop: 1,
                    paddingBottom: 2,
                }}
            >
                <Divider />
            </Grid>
            <Grid size={6}>
                <DialogContentText>{`Name: ${requestToEdit.name}`}</DialogContentText>
            </Grid>
            <Grid size={6}>
                <DialogContentText>
                    {`Email:`}
                    <CopyToClipboardButton text={requestToEdit.email} />
                </DialogContentText>
            </Grid>
            <Grid size={6}>
                <DialogContentText>
                    {`Organization: ${requestToEdit.organization}`}
                </DialogContentText>
            </Grid>
        </>
    );

    if (requestToEdit.status === RequestState.APPROVED) {
        return (
            <Dialog maxWidth="md" fullWidth open onClose={onClose}>
                <DialogTitle>User Access Granted</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={{ marginBottom: '1rem' }}>
                        User was approved for '{requestToEdit.assignedGroupName}' on{' '}
                        {format(new Date(requestToEdit.createdAt), 'MM/dd/yyyy')}
                    </DialogContentText>

                    <Grid
                        container
                        spacing={1}
                        size={12}
                        sx={{
                            marginBottom: 2,
                        }}
                    >
                        {requestDetails}
                        <Grid size={6}>
                            <DialogContentText>
                                {`Access to study: ${requestToEdit.assignedGroupName}`}
                            </DialogContentText>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        );
    }

    if (requestToEdit.status === RequestState.DENIED) {
        return (
            <Dialog maxWidth="md" fullWidth open onClose={onClose}>
                <DialogTitle>User Access Was Denied</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={{ marginBottom: '1rem' }}>
                        An admin denied access to this user on{' '}
                        {format(new Date(requestToEdit.createdAt), 'MM/dd/yyyy')}
                    </DialogContentText>

                    <Grid
                        container
                        spacing={1}
                        size={12}
                        sx={{
                            marginBottom: 2,
                        }}
                    >
                        {requestDetails}
                    </Grid>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog maxWidth="md" fullWidth open onClose={onClose}>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={onSubmit}
                validateOnChange={true}
                validationSchema={createGroupWithUserValidation}
            >
                {({ dirty, errors, isSubmitting, isValid, isValidating, setFieldValue }) => (
                    <Form>
                        <DialogTitle>Allow Access to User</DialogTitle>
                        <DialogContent dividers>
                            <DialogContentText sx={{ marginBottom: '1rem' }}>
                                Enter the name of the Group to create. User will be added as an
                                admin to this new group
                            </DialogContentText>

                            <Grid
                                container
                                spacing={1}
                                size={12}
                                sx={{
                                    marginBottom: 2,
                                }}
                            >
                                {requestDetails}
                                <Grid
                                    size={12}
                                    sx={{
                                        paddingTop: 2,
                                        paddingBottom: 3,
                                    }}
                                >
                                    <Divider />
                                </Grid>
                                <Grid size={12}>
                                    <Field
                                        name="groupName"
                                        label="Project Group Name"
                                        as={TextField}
                                        fullWidth
                                        required
                                        variant="outlined"
                                        error={dirty && !isValidating && !!errors.groupName}
                                        helperText={
                                            isValidating
                                                ? 'Checking availability…'
                                                : dirty && errors.groupName
                                                  ? errors.groupName
                                                  : 'Letters, numbers and spaces only'
                                        }
                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                            setFieldValue('groupName', e.target.value, true)
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="outlined"
                                color="secondary"
                                disabled={isSubmitting || denying}
                                onClick={onClose}
                            >
                                Close
                            </Button>
                            <Button
                                color="error"
                                onClick={() => onDenyAccess()}
                                loading={denying}
                                disabled={isSubmitting}
                                variant="contained"
                            >
                                Deny Access
                            </Button>
                            {/* `type="submit"` so Formik owns the submit: it revalidates first and
                                tracks `isSubmitting`. `isValidating` matters here specifically —
                                the group name uniqueness check is async, and without it a reviewer
                                could authorize while that check was still in flight. */}
                            <Button
                                type="submit"
                                variant="contained"
                                loading={isSubmitting}
                                disabled={!dirty || !isValid || isValidating || denying}
                            >
                                Authorize Access
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

const createGroupWithUserValidation = Yup.object<CreateGroupWithUserForm>({
    groupName: Yup.string()
        .label('Project Group Name')
        .required('Project Group Name is required')
        .min(4, 'Project Group Name must be at least 4 characters')
        // Yup runs custom tests for `undefined` too, so guard rather than calling `.match` on it.
        .test('allowed-characters', 'Letters, numbers and spaces only', (v) =>
            v ? /^[a-zA-Z0-9\s]*$/.test(v) : true,
        )
        .test(
            'group-exists',
            ({ value }) => `A project group named "${value}" already exists`,
            isNewGroup,
        ),
});

export default AccessReviewDialog;
